import React, { useMemo } from 'react';
import { Dropdown } from 'components/Dropdown/Dropdown';
import { CardSizeSelector, CardSize } from 'components/CardSizeSelector/CardSizeSelector';
import SortDirectionToggle from './SortDirectionToggle';
import { SortOrder } from 'types/interfaces/sort';
import {
  SORT_OPTIONS,
  SORT_LABEL_PREFIX,
  getDefaultDirection,
  getOptionByKey,
} from '../sortOptions';
import { FilterToken } from '../searchState';
import FilterTokenRow from './FilterTokenRow';
import './ResultsToolbar.css';

interface ResultsToolbarProps {
  totalCount?: number;
  /** Same rule the grid uses, computed once in SearchWorkbenchPage. */
  isResultsPending: boolean;
  tokens: FilterToken[];
  onClearToken: (token: FilterToken) => void;
  onClearAll: () => void;
  sort: string;
  direction: SortOrder;
  onSortChange: (sort: string, direction: SortOrder) => void;
  lang: string;
  cardSize: CardSize;
  onCardSizeChange: (size: CardSize) => void;
  panelHidden: boolean;
  onTogglePanel: () => void;
  isMobile: boolean;
}

const SEARCH_CARD_SIZES: CardSize[] = ['S', 'M', 'L'];

export const ResultsToolbar: React.FC<ResultsToolbarProps> = ({
  totalCount,
  isResultsPending,
  tokens,
  onClearToken,
  onClearAll,
  sort,
  direction,
  onSortChange,
  lang,
  cardSize,
  onCardSizeChange,
  panelHidden,
  onTogglePanel,
  isMobile,
}) => {
  // Point Value sorting is EN-only on the backend, so it is offered but disabled
  // for a non-English search rather than silently returning the wrong order.
  const isEnglishSearch = !lang || lang === 'en';
  const disabledOptions = useMemo(
    () =>
      isEnglishSearch
        ? []
        : SORT_OPTIONS.filter((o) => o.enOnly).map((o) => `${SORT_LABEL_PREFIX}${o.label}`),
    [isEnglishSearch],
  );

  const sortLabels = useMemo(
    () => SORT_OPTIONS.map((o) => `${SORT_LABEL_PREFIX}${o.label}`),
    [],
  );

  const currentSortLabel = `${SORT_LABEL_PREFIX}${getOptionByKey(sort)?.label ?? ''}`;

  const handleSortLabel = (label: string) => {
    const key = SORT_OPTIONS.find((o) => `${SORT_LABEL_PREFIX}${o.label}` === label)?.key;
    if (key) onSortChange(key, getDefaultDirection(key));
  };

  // Split rather than one string: the number carries the weight and the unit
  // recedes, which a single label cannot express.
  // The old `&& totalCount === undefined` clause meant a filter change kept showing
  // the PREVIOUS query's total instead of saying it was searching. One rule with the
  // grid now: the page decides, the toolbar renders.
  const isSearching = isResultsPending;
  const count = (totalCount ?? 0).toLocaleString();
  const unit = totalCount === 1 ? 'result' : 'results';

  return (
    <div className="results-toolbar">
      <div className="results-toolbar-primary">
        <span className="results-count" data-testid="results-count">
          {isSearching ? (
            'Searching...'
          ) : (
            <>
              {count} <span className="results-count-unit">{unit}</span>
            </>
          )}
        </span>

        {tokens.length > 0 && (
          <span className="results-toolbar-divider" aria-hidden="true" />
        )}

        <FilterTokenRow
          tokens={tokens}
          onClear={onClearToken}
          onClearAll={onClearAll}
          scrollable={isMobile}
        />
      </div>

      <div className="results-toolbar-controls">
        {/* The wrapper is the restyle hook: Dropdown is a MUI Select styled by
            inline sx and shared app-wide, so it is reskinned from the outside
            here rather than changed at the source. */}
        <div className="results-sort-chip">
          <Dropdown
            options={sortLabels}
            value={currentSortLabel}
            onChange={handleSortLabel}
            disabledOptions={disabledOptions}
          />
        </div>
        <SortDirectionToggle
          direction={direction}
          onToggle={() => onSortChange(sort, direction === 'asc' ? 'desc' : 'asc')}
        />
        <CardSizeSelector
          value={cardSize}
          onChange={onCardSizeChange}
          sizes={SEARCH_CARD_SIZES}
        />
        {!isMobile && (
          <button type="button" className="toggle-filters-btn" onClick={onTogglePanel}>
            {panelHidden ? '‹ Show filters' : 'Hide filters ›'}
          </button>
        )}
      </div>
    </div>
  );
};

export default ResultsToolbar;
